/** Reading channels out of a lap document the way the logger does.
 *
 * The Python reads a column two ways and they mean different things:
 * `samples["t"]` is a promise that the channel is there (a KeyError is a bug
 * in the caller), and `samples.get("t") or []` tolerates a recording that
 * never carried it — packet A has no accelerometer, laps stored before #96
 * have no elevation. The two readers here are those two, so each port says
 * which it meant. Both look only at the document's own keys: a channel
 * named `constructor` would otherwise read as a function.
 */

import type { Samples } from './types';

/** `samples.get(name) or None`: the channel, or undefined when the document
 *  has no such channel (or holds something that is not a list under it). */
export function column(samples: Samples, name: string): number[] | undefined {
  if (!Object.hasOwn(samples, name)) return undefined;
  const value: unknown = samples[name];
  return Array.isArray(value) ? (value as number[]) : undefined;
}

/** `samples[name]`: the channel, which the caller has promised is there. */
export function required(samples: Samples, name: string): number[] {
  const value = column(samples, name);
  if (value === undefined) throw new Error(`lap has no "${name}" channel`);
  return value;
}

/** Python's `name in samples`. */
export function has(samples: Samples, name: string): boolean {
  return Object.hasOwn(samples, name);
}
